/**
 * PasswordStrength – Live-Anzeige der Passwortstärke + Regel-Checkliste.
 *
 * Regeln müssen mit dem Backend (utils.validate_password_strength) übereinstimmen:
 * min. 8 Zeichen, Klein-, Großbuchstabe, Ziffer, Sonderzeichen.
 */
import React from 'react';
import { Check, X } from 'lucide-react';

export const PASSWORD_RULES = [
  { key: 'length', label: 'Mindestens 8 Zeichen', test: (p) => p.length >= 8 },
  { key: 'lower', label: 'Ein Kleinbuchstabe', test: (p) => /[a-zäöüß]/.test(p) },
  { key: 'upper', label: 'Ein Großbuchstabe', test: (p) => /[A-ZÄÖÜ]/.test(p) },
  { key: 'digit', label: 'Eine Ziffer', test: (p) => /\d/.test(p) },
  { key: 'special', label: 'Ein Sonderzeichen', test: (p) => /[^A-Za-z0-9äöüÄÖÜß]/.test(p) },
];

export const isPasswordValid = (pw) => PASSWORD_RULES.every((r) => r.test(pw || ''));

const LEVELS = [
  { label: 'Sehr schwach', cls: 'bg-red-500', text: 'text-red-600' },
  { label: 'Schwach', cls: 'bg-orange-500', text: 'text-orange-600' },
  { label: 'Mittel', cls: 'bg-amber-500', text: 'text-amber-600' },
  { label: 'Gut', cls: 'bg-lime-500', text: 'text-lime-600' },
  { label: 'Stark', cls: 'bg-emerald-500', text: 'text-emerald-600' },
];

export const PasswordStrength = ({ password = '' }) => {
  if (!password) return null;
  const passed = PASSWORD_RULES.filter((r) => r.test(password)).length;
  const level = LEVELS[Math.max(0, passed - 1)];

  return (
    <div className="space-y-2" data-testid="password-strength">
      <div className="flex items-center gap-2">
        <div className="flex-1 h-1.5 rounded-full bg-secondary overflow-hidden">
          <div
            className={`h-full transition-all ${level.cls}`}
            style={{ width: `${(passed / PASSWORD_RULES.length) * 100}%` }}
          />
        </div>
        <span className={`text-xs font-medium ${level.text}`} data-testid="password-strength-label">
          {level.label}
        </span>
      </div>
      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
        {PASSWORD_RULES.map((r) => {
          const ok = r.test(password);
          return (
            <li key={r.key} className={`flex items-center gap-1.5 text-xs ${ok ? 'text-emerald-600' : 'text-muted-foreground'}`}>
              {ok ? <Check className="h-3.5 w-3.5" /> : <X className="h-3.5 w-3.5 opacity-50" />}
              {r.label}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default PasswordStrength;
